import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useDrawerDrag } from "../../../hooks/useDrawerDrag";
import {
  X, RefreshCw, Plus, Upload, Download, ArrowDownUp, ArrowUp, ArrowDown,
  FileText,
} from "lucide-react";
import SearchInput from "../../../components/SearchInput/SearchInput";
import Button from "../../../components/Button/Button";
import Pagination from "../../../components/Pagination/Pagination";
import useFitRows from "../../../hooks/useFitRows";
import PageFooter from "../../../components/PageFooter/PageFooter";
import StatusTooltip from "../../../components/StatusTooltip/StatusTooltip";
import DatePicker from "../../../components/DatePicker/DatePicker";
import api from "../../../services/api";
import styles from "./TradeCopier.module.css";
import mStyles from "./TradeCopierMaster.module.css";

const EMPTY_FORM = {
  name: "",
  login: "",
  password: "",
  showPassword: false,
  server: "",
  firstDate: "",
  // Editorial copy for the master's detail screen, all optional: the strategy
  // line badged beside its name, the risk tier pill, and the bio under "About".
  strategy: "",
  risk: "",
  description: "",
  // The deposit every max-drawdown reading for this master is measured against,
  // and the drawdown POW already holds for it (blank = start from today's).
  deposit: "",
  maxDrawdown: "",
};

// Match the caps the API enforces on the editorial fields
// (POST/PATCH /copier/masters).
const DESCRIPTION_MAX = 2000;
const STRATEGY_MAX = 120;

// The tiers the API accepts, and the only ones the pills have colours for.
// Blank means the master shows no risk pill at all.
const RISK_TIERS = ["Low", "Medium", "High"];

// Masters saved before the tiers were renamed still carry the old names; the
// edit form opens on the tier each one now reads as.
const LEGACY_RISK = { conservative: "Low", moderate: "Medium", aggressive: "High" };
const riskTierOf = (risk) => {
  const text = String(risk || "").trim();
  return LEGACY_RISK[text.toLowerCase()]
    || RISK_TIERS.find((t) => t.toLowerCase() === text.toLowerCase())
    || "";
};

const STATUS_LABEL = {
  connected: "Connected",
  error: "Error",
  connecting: "Connecting...",
  disconnected: "Disconnected",
};

// Name text colour is cosmetic (the API doesn't send one) — pick a stable
// colour per account name so it stays consistent across refreshes.
function colorFor(name) {
  return "green";
}

function fmtMoney(v) {
  if (v == null || v === "") return "-";
  return `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

// Starting point only — the live count is measured from the window height so
// the table ends where the screen does. See useFitRows.
const PAGE_SIZE = 10;

// Cadence for the background live poll that keeps balance / equity / connected
// slaves / status current from the webhook-maintained cache.
const AUTO_POLL_MS = 7000;

/* ── Slaves tab: CSV round-trip ──
   Download writes the visible list; Upload sends the same file back as a bulk
   lot-multiplier edit. The columns are the ones the TradeSync copier report
   uses (the CSV POW already gets in Slack), so the two files are the same
   thing and either can be edited and uploaded here. `copier_id` leads the row
   because it is what a re-import matches on: account numbers are the
   human-readable handle but aren't guaranteed unique across the table, and an
   edited name shouldn't lose the row. */
const CSV_HEADER = [
  "copier_id",
  "master",
  "lead_id",
  "slave",
  "follower_id",
  "follower_name",
  "balance",
  "equity",
  "risk_value",
];

function csvEscape(v) {
  const cell = v == null ? "" : String(v);
  return /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
}

function slavesToCsv(rows) {
  const lines = [CSV_HEADER.join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.copierId ?? "",
        r.master ?? "",
        r.leadId ?? "",
        r.login ?? "",
        r.followerId ?? "",
        r.name ?? "",
        r.balance ?? "",
        r.equity ?? "",
        r.mult,
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  return lines.join("\r\n");
}

// Minimal RFC-4180 reader: enough for the file we emit plus whatever a
// spreadsheet hands back (quoted fields, doubled quotes, CRLF or LF).
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c !== '"') { field += c; continue; }
      if (text[i + 1] === '"') { field += '"'; i++; continue; }
      quoted = false;
      continue;
    }
    if (c === '"') { quoted = true; continue; }
    if (c === ",") { row.push(field); field = ""; continue; }
    if (c === "\r") continue;
    if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; continue; }
    field += c;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows;
}

function fmtMoney2(v) {
  if (v == null || v === "") return "-";
  return `$${Number(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Two-letter stand-in for a name on a phone, matching the masters table.
const shortOf = (name) => String(name || "").slice(0, 2).toUpperCase();

// Sort arrow for a slaves-tab column: the neutral affordance until the column
// is the one being sorted on.
function SortArrow({ sort, col }) {
  if (sort.col !== col) return <ArrowDownUp size={12} className={styles.sortIcon} />;
  return sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
}

function MasterStatusBadge({ status }) {
  const safeStatus = STATUS_LABEL[status] ? status : "disconnected";

  return (
    <StatusTooltip label={STATUS_LABEL[safeStatus]}>
      <span className={`${mStyles.statusBadge} ${mStyles[`status_${safeStatus}`]}`}>
        <span className={`${mStyles.statusDot} ${mStyles[`dot_${safeStatus}`]}`} />
        <span className={mStyles.badgeLabel}>{STATUS_LABEL[safeStatus]}</span>
      </span>
    </StatusTooltip>
  );
}

function TradeCopierMaster() {
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState("masters"); // 'masters' | 'slaves'

  // ── Slaves tab ──
  // Every connected slave platform-wide. Loaded the first time the tab is
  // opened rather than on mount: the masters table is what the page lands on.
  const [slaves, setSlaves] = useState([]);
  const [slavesLoading, setSlavesLoading] = useState(false);
  const [slavesError, setSlavesError] = useState("");
  const [slavesLoaded, setSlavesLoaded] = useState(false);
  const [slaveSort, setSlaveSort] = useState({ col: null, dir: "asc" });
  // ── Upload flow ──
  // The file is chosen in its own modal and only sent on Confirm, so an admin
  // can see what they picked (and swap it) before anything is written.
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  // The "Overriding settings…" notice: { text, done } while a file is applied.
  const [toast, setToast] = useState(null);
  // { updated, unchanged, skipped } from a finished import, or { error }. Only
  // opened when there is something the toast can't say on its own.
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);
  const toastTimer = useRef(null);

  useEffect(() => () => clearTimeout(toastTimer.current), []);
  const [modal, setModal] = useState(null); // null | 'add' | 'edit' | 'remove'
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeError, setRemoveError] = useState("");
  // Password as loaded for the master being edited, so an untouched field
  // doesn't re-send credentials (which makes TradeSync reconnect the account).
  const [initialPassword, setInitialPassword] = useState("");
  const editIdRef = useRef(null);

  // `force` (the refresh button) asks the API to pull live from TradeSync
  // instead of serving the webhook-maintained cache.
  const fetchMasters = useCallback(async (force = false) => {
    try {
      const { data } = await api.get("/copier/masters", {
        params: force ? { refresh: 1 } : undefined,
      });
      setMasters(Array.isArray(data) ? data : []);
      setApiError("");
    } catch {
      setApiError("Failed to load master accounts");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchMasters();
  }, [fetchMasters]);

  const fetchSlaves = useCallback(async () => {
    setSlavesLoading(true);
    try {
      const { data } = await api.get("/copier/all-slaves");
      setSlaves(Array.isArray(data) ? data : []);
      setSlavesError("");
    } catch (err) {
      setSlavesError(
        err.response?.data?.error || "Failed to load slave accounts",
      );
    } finally {
      setSlavesLoading(false);
      setSlavesLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (tab !== "slaves" || slavesLoaded) return;
    fetchSlaves();
  }, [tab, slavesLoaded, fetchSlaves]);

  // Both tables paginate off one `page`, so a tab switch has to start over —
  // page 4 of the masters is rarely page 4 of anything on the other tab.
  useEffect(() => { setPage(1); }, [tab]);

  // Live auto-update: while the page is open, poll the webhook-maintained cache
  // for every master so balance / equity / connected-slaves / status stay
  // current on screen without a manual reload (no TradeSync call per tick — the
  // webhook keeps the cache fresh).
  useEffect(() => {
    // Only while the masters table is the one on screen — the poll exists to
    // keep its cells current.
    if (tab !== "masters") return;
    const ids = masters.map((m) => m.id).filter(Boolean);
    if (!ids.length) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/copier/masters/live", {
          params: { ids: ids.join(",") },
        });
        if (Array.isArray(data) && data.length) {
          setMasters((prev) =>
            prev.map((m) => {
              const upd = data.find((d) => d.id === m.id);
              return upd ? { ...m, ...upd } : m;
            }),
          );
        }
      } catch {
        /* transient; retry next tick */
      }
    }, AUTO_POLL_MS);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masters, tab]);

  // Refresh button: force a live TradeSync pull for the whole list.
  function refresh() {
    setRefreshing(true);
    fetchMasters(true);
  }

  const filtered = useMemo(
    () =>
      masters.filter(
        (m) =>
          (m.name || "").toLowerCase().includes(search.toLowerCase()) ||
          String(m.login || "").includes(search),
      ),
    [masters, search],
  );

  // The one search box serves both tabs; slaves match on name or account number.
  const filteredSlaves = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? slaves.filter(
          (sl) =>
            (sl.name || "").toLowerCase().includes(q) ||
            String(sl.login || "").includes(search.trim()),
        )
      : slaves;
    if (!slaveSort.col) return base;

    const dir = slaveSort.dir === "asc" ? 1 : -1;
    // A slave with no live metrics row has no balance or equity; park those at
    // the bottom of either direction rather than letting them lead an
    // ascending sort.
    const num = (v) => (v == null ? null : Number(v));
    return [...base].sort((a, b) => {
      if (slaveSort.col === "master") {
        return String(a.master || "").localeCompare(String(b.master || "")) * dir;
      }
      const [av, bv] =
        slaveSort.col === "balance"
          ? [num(a.balance), num(b.balance)]
          : slaveSort.col === "equity"
            ? [num(a.equity), num(b.equity)]
            : [num(a.mult) ?? 1, num(b.mult) ?? 1];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * dir;
    });
  }, [slaves, search, slaveSort]);

  function handleSlaveSort(col) {
    setSlaveSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" },
    );
  }

  const paginationRef = useRef(null);
  const footerRef = useRef(null);
  const { bodyRef, rowsPerPage } = useFitRows({
    fallback: PAGE_SIZE,
    belowRefs: [paginationRef, footerRef],
    min: 6,
    max: 30,
    // 24px .content bottom padding + 4px .tableWrap margin, neither of which
    // either ref covers. (The larger mobile margin doesn't matter — the hook
    // pins phones to the fixed size.)
    gutter: 28,
  });

  // Only one table is mounted at a time, so the fit-rows measurement and the
  // pagination below it both follow whichever tab is open.
  const rows = tab === "masters" ? filtered : filteredSlaves;
  const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  /* Exports what the tab currently shows — search and sort included — so the
     file matches the list the admin is looking at rather than the whole table. */
  function handleDownload() {
    const blob = new Blob([slavesToCsv(filteredSlaves)], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "POW-slave-accounts.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function openUpload() {
    setUploadFile(null);
    setDragOver(false);
    setUploadOpen(true);
  }

  function closeUpload() {
    setUploadOpen(false);
    setUploadFile(null);
    setDragOver(false);
  }

  function showToast(text, done) {
    clearTimeout(toastTimer.current);
    setToast({ text, done });
    if (done) toastTimer.current = setTimeout(() => setToast(null), 2600);
  }

  /* Turns the chosen sheet into the bulk edit the API takes. Only the risk
     column is acted on — the rest are there so the file reads like the table it
     came from — and a row is matched on whichever handle it still carries, in
     the order the API resolves them: the copier, the follower account, the POW
     account, then the account number.

     The header names come from the copier report, but the older export's names
     are accepted too, so a file downloaded before this change still applies. */
  function readUpdates(text) {
    const table = parseCsv(text);
    const head = (table[0] || []).map((h) =>
      h.trim().toLowerCase().replace(/\s+/g, "_"),
    );
    const col = (...names) => {
      for (const n of names) {
        const i = head.indexOf(n);
        if (i !== -1) return i;
      }
      return -1;
    };

    const iCopier = col("copier_id");
    const iFollower = col("follower_id");
    const iId = col("id");
    const iLogin = col("slave", "account_number");
    const iRisk = col("risk_value", "risk_multiplier");
    if (iRisk === -1 || (iCopier === -1 && iFollower === -1 && iId === -1 && iLogin === -1)) {
      return {
        error:
          "That file is missing the columns needed. Download the list first and edit the risk_value column.",
      };
    }

    const cell = (r, i) => (i !== -1 ? String(r[i] ?? "").trim() : "");
    const updates = table
      .slice(1)
      .filter((r) => r.some((c) => c.trim() !== ""))
      .map((r) => ({
        ...(cell(r, iCopier) ? { copierId: cell(r, iCopier) } : {}),
        ...(cell(r, iFollower) ? { followerId: cell(r, iFollower) } : {}),
        ...(cell(r, iId) ? { id: cell(r, iId) } : {}),
        ...(cell(r, iLogin) ? { login: cell(r, iLogin) } : {}),
        riskValue: Number(cell(r, iRisk)),
      }));

    if (!updates.length) return { error: "That file has no rows to apply." };
    return { updates };
  }

  /* Confirm closes the modal and hands over to the toast — the write can take a
     while (one TradeSync call per changed row) and there is nothing left to
     decide, so it runs behind the table rather than behind a spinner. The
     result modal only opens when the toast can't tell the whole story: rows the
     API refused, or a file that never made it off the client. */
  async function handleConfirmUpload() {
    if (!uploadFile) return;
    closeUpload();
    showToast("Overriding settings…", false);
    try {
      const { updates, error } = readUpdates(await uploadFile.text());
      if (error) {
        setToast(null);
        setImportResult({ error });
        return;
      }

      const { data } = await api.post("/copier/all-slaves/bulk-risk", { updates });
      await fetchSlaves();

      if (data?.skipped?.length) {
        setToast(null);
        setImportResult(data);
      } else {
        showToast(
          data?.updated ? `${data.updated} accounts updated` : "No changes to apply",
          true,
        );
      }
    } catch (err) {
      setToast(null);
      setImportResult({
        error: err.response?.data?.error || "Failed to apply that file.",
      });
    }
  }

  function openAdd() {
    setForm(EMPTY_FORM);
    setEditTarget(null);
    setFormError("");
    setSubmitted(false);
    setModal("add");
  }

  function openEdit(master) {
    setEditTarget(master);
    editIdRef.current = master.id;
    setInitialPassword("");
    setForm({
      name: master.name || "",
      login: master.login != null ? String(master.login) : "",
      password: "", // filled in below — it's kept off the list payload
      showPassword: false,
      server: master.server || "",
      firstDate: master.firstDate || "",
      strategy: master.strategy || "",
      risk: riskTierOf(master.risk),
      description: master.description || "",
      deposit: master.deposit != null ? String(master.deposit) : "",
      maxDrawdown: master.maxDD != null ? String(master.maxDD) : "",
    });
    // The password is admin-only, so it comes from its own endpoint rather than
    // the masters list. Ignore a response that lands after the user has moved on.
    api
      .get(`/copier/masters/${master.id}/password`)
      .then(({ data }) => {
        const pwd = data?.password || "";
        if (!pwd || editIdRef.current !== master.id) return;
        setInitialPassword(pwd);
        setForm((f) => ({ ...f, password: pwd }));
      })
      .catch(() => {
        /* leave the field empty — saving without it keeps the stored password */
      });
    setFormError("");
    setRemoveError("");
    setSubmitted(false);
    setModal("edit");
  }

  function closeModal() {
    setModal(null);
    setEditTarget(null);
    editIdRef.current = null;
    setInitialPassword("");
    setSubmitted(false);
    setFormError("");
    setRemoveError("");
  }
  const { modalRef: masterDrawerRef, dragHandleProps: masterDragHandle } = useDrawerDrag(closeModal);

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleAdd() {
    setSubmitted(true);
    setFormError("");
    const { name, login, password, server, firstDate, strategy, risk, description, deposit, maxDrawdown } = form;
    if (!name || !login || !password || !server || !firstDate || !deposit) return;
    setSubmitLoading(true);
    try {
      await api.post("/copier/masters", {
        name,
        login,
        password,
        server,
        firstDate,
        // Both optional — left out, the detail screen falls back to its built-in
        // copy for the strategy.
        ...(strategy.trim() ? { strategy } : {}),
        ...(risk ? { risk } : {}),
        ...(description.trim() ? { description } : {}),
        deposit,
        // Blank means "no figure from POW" — the tracker starts from the
        // drawdown it reads on its first pass.
        ...(maxDrawdown !== "" ? { maxDrawdown } : {}),
      });
      await fetchMasters();
      closeModal();
    } catch (err) {
      setFormError(err.response?.data?.error || "Failed to add master account");
    } finally {
      setSubmitLoading(false);
    }
  }

  // Edit: PATCH the account. The password only goes up when it actually changed
  // — an untouched (or not-yet-loaded) field keeps the stored one.
  async function handleSave() {
    if (!editTarget) return;
    setSubmitted(true);
    setFormError("");
    const { name, login, password, server, firstDate, strategy, risk, description, deposit, maxDrawdown } = form;
    if (!name || !login || !server || !firstDate || !deposit) return;
    setSubmitLoading(true);
    try {
      await api.patch(`/copier/masters/${editTarget.id}`, {
        name,
        login,
        server,
        firstDate,
        // Always sent, so emptying any of them clears the stored value.
        strategy,
        risk,
        description,
        ...(password && password !== initialPassword ? { password } : {}),
        // Both drawdown fields only go up when they actually changed: the stored
        // max drawdown is a live value the tracker keeps lowering, so re-sending
        // the figure the modal opened with would undo a reading taken since.
        ...(deposit !== String(editTarget.deposit ?? "") ? { deposit } : {}),
        ...(maxDrawdown !== String(editTarget.maxDD ?? "") ? { maxDrawdown } : {}),
      });
      await fetchMasters();
      closeModal();
    } catch (err) {
      setFormError(err.response?.data?.error || "Failed to save changes");
    } finally {
      setSubmitLoading(false);
    }
  }

  async function handleRemove() {
    if (!editTarget) return;
    setRemoveLoading(true);
    setRemoveError("");
    try {
      await api.delete(`/copier/masters/${editTarget.id}`);
      await fetchMasters();
      closeModal();
    } catch (err) {
      setRemoveError(
        err.response?.data?.error ||
          (err.response?.status === 409
            ? "Cannot remove a master with open trades."
            : "Failed to remove master account"),
      );
      setModal("edit"); // surface the error back on the edit modal
    } finally {
      setRemoveLoading(false);
    }
  }

  function fieldErr(key) {
    // On edit, a blank password is valid — it means "keep the existing one".
    if (key === "password" && modal === "edit") return false;
    return submitted && !form[key];
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Trade copier</h1>
        {/* Adding a master is a Masters-tab action; the Slaves tab puts its own
            controls (Upload / Download) beside the tabs instead. The button
            still holds its box on the other tab (see .tabOnly) so switching
            tabs doesn't slide the tabs and the table up and down. */}
        <div className={tab === "masters" ? undefined : mStyles.tabOnly}>
          <Button variant="primary" size="sm" fullWidth={false} onClick={openAdd}>
            <Plus size={16} /> Add master
          </Button>
        </div>
      </div>

      {/* Search — one box for both tabs */}
      <SearchInput
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={
          tab === "masters" ? "Search account" : "Search account name or number"
        }
        className={styles.searchWrap}
      />

      {/* Tabs */}
      <div className={mStyles.tabRow}>
        <div className={`${styles.tabs} ${mStyles.tabsFlush}`} role="tablist">
          <button
            role="tab"
            aria-selected={tab === "masters"}
            className={`${styles.tab} ${tab === "masters" ? styles.tabActive : ""}`}
            onClick={() => setTab("masters")}
          >
            Masters
          </button>
          <button
            role="tab"
            aria-selected={tab === "slaves"}
            className={`${styles.tab} ${tab === "slaves" ? styles.tabActive : ""}`}
            onClick={() => setTab("slaves")}
          >
            Slaves
          </button>
        </div>

        {/* Slaves-tab actions. Kept in the layout on the Masters tab too — they
            are the tallest thing in this row, so unmounting them would shorten
            it and take the tabs and the table with it. */}
        <div
          className={`${mStyles.ioActions} ${
            tab === "slaves" ? "" : mStyles.tabOnly
          }`}
        >
          <button
            className={mStyles.ioBtn}
            onClick={openUpload}
            title="Upload a sheet to update these slaves"
          >
            <Upload size={16} />
            <span className={styles.desktopOnly}>Upload</span>
          </button>
          <button
            className={mStyles.ioBtn}
            onClick={handleDownload}
            disabled={!filteredSlaves.length}
            title="Download this table as a spreadsheet"
          >
            <Download size={16} />
            <span className={styles.desktopOnly}>Download</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className={`${styles.tableWrap} ${mStyles.masterTableWrap}`}>
        <div className={mStyles.tableScroll}>
          {tab === "masters" ? (
            <table
              className={`${styles.table} ${mStyles.masterTable} ${
                totalPages > 1 ? mStyles.paginated : ""
              }`}
            >
              <thead>
                <tr>
                  <th className={styles.th}>Name</th>
                  <th className={`${styles.th} ${styles.hideOnMobile}`}>Login</th>
                  <th className={`${styles.th} ${styles.hideOnMobile}`}>Balance</th>
                  <th className={`${styles.th} ${mStyles.equityCol}`}>Equity</th>
                  <th className={`${styles.th} ${styles.hideOnMobile}`}>
                    Connected slaves
                  </th>
                  <th className={`${styles.th} ${mStyles.statusCol}`}>Status</th>
                  {/* Mobile-only refresh column. It comes before Action so that on
                      a phone the refresh button sits at the edge of the viewport
                      and Manage is what the horizontal scroll reveals. Hidden on
                      desktop, where refresh lives inside the action cell. */}
                  <th
                    className={`${styles.th} ${mStyles.mobileRefreshTh}`}
                    aria-hidden="true"
                  />
                  <th
                    className={`${styles.th} ${styles.thAction} ${mStyles.actionCol}`}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody ref={bodyRef}>
                {loading ? (
                  <tr>
                    <td colSpan={8} className={styles.emptyState}>
                      Loading master accounts…
                    </td>
                  </tr>
                ) : apiError ? (
                  <tr>
                    <td colSpan={8} className={styles.emptyState}>
                      {apiError}
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className={styles.emptyState}>
                      {search
                        ? `No results found for "${search}"`
                        : "No master accounts yet. Add one to get started."}
                    </td>
                  </tr>
                ) : (
                  pageRows.map((m) => {
                    const color = colorFor(m.name);
                    return (
                      <tr
                        key={m.id}
                        className={`${styles.row} ${mStyles.masterRow}`}
                        onClick={() => openEdit(m)}
                      >
                        <td className={`${styles.td} ${styles[`master_${color}`]}`}>
                          <span className={styles.desktopOnly}>{m.name}</span>
                          <span className={styles.mobileOnly}>
                            {String(m.name || "").slice(0, 2).toUpperCase()}
                          </span>
                        </td>
                        <td className={`${styles.td} ${styles.hideOnMobile}`}>
                          {m.login}
                        </td>
                        <td className={`${styles.td} ${styles.hideOnMobile}`}>
                          {fmtMoney(m.balance)}
                        </td>
                        <td className={`${styles.td} ${mStyles.equityCol}`}>
                          {fmtMoney(m.equity)}
                        </td>
                        <td className={`${styles.td} ${styles.hideOnMobile}`}>
                          {m.slaves ?? 0}
                        </td>
                        <td className={`${styles.td} ${mStyles.statusCol}`}>
                          <MasterStatusBadge status={m.status} />
                        </td>
                        <td className={`${styles.td} ${mStyles.mobileRefreshTd}`}>
                          <button
                            className={mStyles.refreshBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              refresh();
                            }}
                            title="Refresh"
                            disabled={refreshing}
                          >
                            <RefreshCw
                              size={20}
                              className={refreshing ? mStyles.spinning : undefined}
                            />
                          </button>
                        </td>
                        <td
                          className={`${styles.td} ${styles.thAction} ${mStyles.actionCol}`}
                        >
                          <div className={mStyles.actionCell}>
                            <button
                              className={mStyles.refreshBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                refresh();
                              }}
                              title="Refresh"
                              disabled={refreshing}
                            >
                              <RefreshCw
                                size={15}
                                className={refreshing ? mStyles.spinning : undefined}
                              />
                            </button>
                            <button
                              className={styles.manageBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(m);
                              }}
                            >
                              Manage
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          ) : (
            <table
              className={`${styles.table} ${mStyles.masterTable} ${mStyles.slaveTable} ${
                totalPages > 1 ? mStyles.paginated : ""
              }`}
            >
              <thead>
                <tr>
                  <th className={`${styles.th} ${styles.hideOnMobile}`}>
                    Account name
                  </th>
                  <th className={styles.th}>
                    <span className={styles.desktopOnly}>Account number</span>
                    <span className={styles.mobileOnly}>Account no.</span>
                  </th>
                  <th className={`${styles.th} ${styles.hideOnMobile}`}>
                    <button
                      className={styles.thBtn}
                      onClick={() => handleSlaveSort("balance")}
                    >
                      Balance
                      <SortArrow sort={slaveSort} col="balance" />
                    </button>
                  </th>
                  <th className={styles.th}>
                    <button
                      className={styles.thBtn}
                      onClick={() => handleSlaveSort("equity")}
                    >
                      Equity
                      <SortArrow sort={slaveSort} col="equity" />
                    </button>
                  </th>
                  <th className={styles.th}>
                    <button
                      className={styles.thBtn}
                      onClick={() => handleSlaveSort("mult")}
                    >
                      <span className={styles.desktopOnly}>Risk multiplier</span>
                      <span className={styles.mobileOnly}>RM</span>
                      <SortArrow sort={slaveSort} col="mult" />
                    </button>
                  </th>
                  <th className={styles.th}>
                    <button
                      className={styles.thBtn}
                      onClick={() => handleSlaveSort("master")}
                    >
                      <span className={styles.desktopOnly}>Connected master</span>
                      <span className={styles.mobileOnly}>Master</span>
                      <SortArrow sort={slaveSort} col="master" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody ref={bodyRef}>
                {slavesLoading ? (
                  <tr>
                    <td colSpan={6} className={styles.emptyState}>
                      Loading slave accounts…
                    </td>
                  </tr>
                ) : slavesError ? (
                  <tr>
                    <td colSpan={6} className={styles.emptyState}>
                      {slavesError}
                    </td>
                  </tr>
                ) : filteredSlaves.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={styles.emptyState}>
                      {search
                        ? `No results found for "${search}"`
                        : "No slave accounts are copying a master yet."}
                    </td>
                  </tr>
                ) : (
                  pageRows.map((sl) => (
                    <tr
                      key={sl.copierId ?? sl.id ?? sl.login}
                      className={`${styles.row} ${mStyles.slaveRow}`}
                    >
                      <td className={`${styles.td} ${styles.hideOnMobile}`}>
                        {sl.name}
                      </td>
                      <td className={styles.td}>{sl.login ?? "-"}</td>
                      <td className={`${styles.td} ${styles.hideOnMobile}`}>
                        {fmtMoney2(sl.balance)}
                      </td>
                      <td className={styles.td}>{fmtMoney2(sl.equity)}</td>
                      <td className={styles.td}>{sl.mult}</td>
                      <td
                        className={`${styles.td} ${styles[`master_${colorFor(sl.master)}`]}`}
                      >
                        {sl.master ? (
                          <>
                            <span className={styles.desktopOnly}>{sl.master}</span>
                            <span className={styles.mobileOnly}>
                              {shortOf(sl.master)}
                            </span>
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        <div ref={paginationRef}>
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onPageChange={setPage}
            variant="outline"
          />
        </div>
      </div>

      <PageFooter ref={footerRef} />

      {/* ── Upload file ── */}
      {uploadOpen && (
        <div className={styles.overlay} onClick={closeUpload}>
          <div
            className={`${styles.modal} ${mStyles.uploadModal}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Upload file</h2>
              <button className={styles.closeBtn} onClick={closeUpload}>
                <X size={18} />
              </button>
            </div>
            <hr className={styles.headerDivider} />

            <div className={`${styles.modalBody} ${mStyles.uploadBody}`}>
              <p className={mStyles.attachLabel}>Attachment</p>
              <p className={mStyles.attachHint}>
                The columns on your file should align with the slave sub menu.
              </p>

              {uploadFile ? (
                <div className={mStyles.fileCard}>
                  <FileText size={22} className={mStyles.fileIcon} />
                  <span className={mStyles.fileName}>{uploadFile.name}</span>
                  <button
                    className={mStyles.replaceBtn}
                    onClick={() => fileRef.current?.click()}
                  >
                    Replace
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className={`${mStyles.dropZone} ${dragOver ? mStyles.dropZoneOver : ""}`}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    const file = e.dataTransfer?.files?.[0];
                    if (file) setUploadFile(file);
                  }}
                >
                  <Upload size={26} className={mStyles.dropIcon} />
                  <span className={mStyles.dropText}>Upload or drag file</span>
                </button>
              )}

              {/* Cleared after every pick so choosing the same file twice —
                  after a Replace, say — still fires a change event. */}
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className={mStyles.fileInput}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) setUploadFile(file);
                }}
              />
            </div>

            <div className={`${styles.modalFooter} ${mStyles.insetFooter} ${mStyles.addFooter}`}>
              <button className={mStyles.cancelBtn} onClick={closeUpload}>
                Cancel
              </button>
              <button
                className={mStyles.submitBtn}
                onClick={handleConfirmUpload}
                disabled={!uploadFile}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Applying an uploaded sheet ──
          Not a blocking modal: the write is one TradeSync call per changed row,
          and there is nothing left for the admin to decide while it runs. */}
      {toast && (
        <div className={mStyles.toast} role="status" aria-live="polite">
          <span className={mStyles.toastText}>{toast.text}</span>
          <span
            className={`${mStyles.toastBar} ${toast.done ? mStyles.toastBarDone : ""}`}
          />
        </div>
      )}

      {/* ── Bulk import result ──
          A CSV can be part-applied, so the summary reports what landed and
          names every row that didn't rather than just succeeding or failing. */}
      {importResult && (
        <div className={styles.overlay} onClick={() => setImportResult(null)}>
          <div
            className={`${styles.modal} ${mStyles.removeModal}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {importResult.error ? "Import failed" : "Import complete"}
              </h2>
              <button
                className={styles.closeBtn}
                onClick={() => setImportResult(null)}
              >
                <X size={18} />
              </button>
            </div>
            <hr className={styles.headerDivider} />

            <div className={styles.modalBody}>
              {importResult.error ? (
                <p className={mStyles.fieldError}>{importResult.error}</p>
              ) : (
                <>
                  <p className={mStyles.importSummary}>
                    {importResult.updated} updated
                    {importResult.unchanged
                      ? `, ${importResult.unchanged} already at that multiplier`
                      : ""}
                    {importResult.skipped?.length
                      ? `, ${importResult.skipped.length} skipped`
                      : ""}
                    .
                  </p>
                  {!!importResult.skipped?.length && (
                    <ul className={mStyles.importSkipped}>
                      {importResult.skipped.map((sk, i) => (
                        <li key={`${sk.row}-${i}`}>
                          <strong>{sk.row}</strong> — {sk.reason}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>

            <div className={`${styles.modalFooter} ${mStyles.insetFooter} ${mStyles.removeFooter}`}>
              <button
                className={mStyles.cancelBtn}
                onClick={() => setImportResult(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit master modal ── */}
      {(modal === "add" || modal === "edit") && (
        <div className={styles.overlay} onClick={closeModal}>
          <div
            className={`${styles.modal} ${mStyles.formModal}`}
            ref={masterDrawerRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={mStyles.pullHandle} {...masterDragHandle} />
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {modal === "add" ? "Add master account" : "Edit master account"}
              </h2>
              <button className={styles.closeBtn} onClick={closeModal}>
                <X size={18} />
              </button>
            </div>
            <hr className={styles.headerDivider} />

            <div className={styles.modalBody}>
              {/* Account name */}
              <div className={mStyles.fieldGroup}>
                <label className={mStyles.fieldLabel}>Account name</label>
                <input
                  className={`${mStyles.fieldInput}${fieldErr("name") ? ` ${mStyles.fieldInputError}` : ""}`}
                  placeholder="e.g. Foundry"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                />
                {fieldErr("name") && <span className={mStyles.fieldError}>Required</span>}
              </div>

              {/* Login + Password */}
              <div className={mStyles.fieldRow}>
                <div className={mStyles.fieldGroup}>
                  <label className={mStyles.fieldLabel}>Login</label>
                  <input
                    className={`${mStyles.fieldInput}${fieldErr("login") ? ` ${mStyles.fieldInputError}` : ""}`}
                    placeholder="MT5 account number"
                    value={form.login}
                    onChange={(e) => setField("login", e.target.value)}
                  />
                  {fieldErr("login") && <span className={mStyles.fieldError}>Required</span>}
                </div>
                <div className={mStyles.fieldGroup}>
                  <label className={mStyles.fieldLabel}>Password</label>
                  <input
                    className={`${mStyles.fieldInput}${fieldErr("password") ? ` ${mStyles.fieldInputError}` : ""}`}
                    type={form.showPassword ? "text" : "password"}
                    placeholder="MT5 password"
                    value={form.password}
                    onChange={(e) => setField("password", e.target.value)}
                  />
                  {fieldErr("password") && <span className={mStyles.fieldError}>Required</span>}
                  <label className={mStyles.showPwdLabel}>
                    <input
                      type="checkbox"
                      className={mStyles.showPwdCheck}
                      checked={form.showPassword}
                      onChange={(e) => setField("showPassword", e.target.checked)}
                    />
                    Show password
                  </label>
                </div>
              </div>

              {/* Server */}
              <div className={mStyles.fieldGroup}>
                <label className={mStyles.fieldLabel}>Server</label>
                <input
                  className={`${mStyles.fieldInput}${fieldErr("server") ? ` ${mStyles.fieldInputError}` : ""}`}
                  placeholder="Type in broker server"
                  value={form.server}
                  onChange={(e) => setField("server", e.target.value)}
                />
                {fieldErr("server") && <span className={mStyles.fieldError}>Required</span>}
              </div>

              {/* First trade date */}
              <div className={mStyles.fieldGroup}>
                <label className={mStyles.fieldLabel}>First trade date</label>
                <DatePicker
                  value={form.firstDate}
                  onChange={(v) => setField("firstDate", v)}
                  placeholder="Select date"
                  error={fieldErr("firstDate")}
                />
                {fieldErr("firstDate") && <span className={mStyles.fieldError}>Required</span>}
                <p className={mStyles.fieldHint}>
                  Trades before this date are ignored for copying and history.
                </p>
              </div>

              {/* Deposit + Max drawdown — the drawdown baseline and the figure
                  measured against it. Max DD is left to the tracker unless POW
                  already holds one for this master. */}
              <div className={mStyles.fieldRow}>
                <div className={mStyles.fieldGroup}>
                  <label className={mStyles.fieldLabel}>Deposit</label>
                  <input
                    className={`${mStyles.fieldInput}${fieldErr("deposit") ? ` ${mStyles.fieldInputError}` : ""}`}
                    type="number"
                    placeholder="e.g. 100000"
                    value={form.deposit}
                    onChange={(e) => setField("deposit", e.target.value)}
                  />
                  {fieldErr("deposit") && <span className={mStyles.fieldError}>Required</span>}
                </div>
                <div className={mStyles.fieldGroup}>
                  <label className={mStyles.fieldLabel}>Max drawdown (%)</label>
                  <input
                    className={mStyles.fieldInput}
                    type="number"
                    step="0.01"
                    placeholder="e.g. -5.1"
                    value={form.maxDrawdown}
                    onChange={(e) => setField("maxDrawdown", e.target.value)}
                  />
                </div>
              </div>
              <p className={mStyles.fieldHint}>
                Max drawdown is measured against the deposit and only ever gets
                worse on its own. Set it here to correct it to the figure you
                already hold; leave it blank to start from the current reading.
              </p>

              {/* Strategy + Description: the only fields here that followers
                  ever read. The first is badged beside the master's name on its
                  detail screen, the second fills the "About" card. Left blank,
                  each falls back to the built-in copy for the strategy
                  (masterProfiles); About hides entirely when there is none. */}
              <div className={mStyles.fieldGroup}>
                <label className={mStyles.fieldLabel}>Strategy</label>
                <input
                  className={mStyles.fieldInput}
                  maxLength={STRATEGY_MAX}
                  placeholder="e.g. Third-party strategy · Institutional grade"
                  value={form.strategy}
                  onChange={(e) => setField("strategy", e.target.value)}
                />
                <p className={mStyles.fieldHint}>
                  The badge beside this master’s name on its detail page — how
                  the strategy is described in a line.
                </p>
              </div>

              {/* Risk tier. A fixed set rather than free text: the pill on the
                  master card and on the detail page colours itself from the
                  tier, and an unrecognised label would render neutral. */}
              <div className={mStyles.fieldGroup}>
                <label className={mStyles.fieldLabel}>Risk type</label>
                <select
                  className={`${mStyles.fieldInput} ${mStyles.fieldSelect}`}
                  value={form.risk}
                  onChange={(e) => setField("risk", e.target.value)}
                >
                  <option value="">No risk pill</option>
                  {RISK_TIERS.map((tier) => (
                    <option key={tier} value={tier}>{tier}</option>
                  ))}
                </select>
                <p className={mStyles.fieldHint}>
                  Shown as a pill on this master’s card in the trade copier and
                  beside its name on the detail page.
                </p>
              </div>

              <div className={mStyles.fieldGroup}>
                <label className={mStyles.fieldLabel}>Description</label>
                <textarea
                  className={`${mStyles.fieldInput} ${mStyles.fieldTextarea}`}
                  rows={5}
                  maxLength={DESCRIPTION_MAX}
                  placeholder="e.g. Atlas is an in-house POW trading system. The strategy applies a rules-based mean reversion methodology across a diversified portfolio of FX pairs…"
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                />
                <p className={mStyles.fieldHint}>
                  Shown under “About” on this master’s detail page. Leave blank
                  to hide the card. {form.description.length}/{DESCRIPTION_MAX}
                </p>
              </div>

              {(formError || removeError) && (
                <p className={mStyles.fieldError} style={{ marginTop: 8 }}>
                  {formError || removeError}
                </p>
              )}
            </div>

            {modal === "add" ? (
              <div
                className={`${styles.modalFooter} ${mStyles.insetFooter} ${mStyles.addFooter}`}
              >
                <button className={mStyles.cancelBtn} onClick={closeModal} disabled={submitLoading}>
                  Cancel
                </button>
                <button className={mStyles.submitBtn} onClick={handleAdd} disabled={submitLoading}>
                  {submitLoading ? "Adding…" : "Add master account"}
                </button>
              </div>
            ) : (
              <div
                className={`${styles.modalFooter} ${mStyles.insetFooter} ${mStyles.editFooter}`}
              >
                <button
                  className={`${mStyles.removeMasterBtn} ${mStyles.removeMasterBtnFull}`}
                  onClick={() => { setRemoveError(""); setModal("remove"); }}
                  disabled={editTarget?.openTrades}
                  title={editTarget?.openTrades ? "Cannot remove while trades are open" : undefined}
                >
                  Remove master
                </button>
                <div className={mStyles.editBtnRow}>
                  <button
                    className={mStyles.submitBtn}
                    onClick={handleSave}
                    disabled={submitLoading}
                  >
                    {submitLoading ? "Saving…" : "Save"}
                  </button>
                  <button
                    className={mStyles.cancelBtn}
                    onClick={closeModal}
                    disabled={submitLoading}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Remove master confirmation ── */}
      {modal === "remove" && (
        <div className={styles.overlay} onClick={closeModal}>
          <div
            className={`${styles.modal} ${mStyles.removeModal}`}
            ref={masterDrawerRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={mStyles.pullHandle} {...masterDragHandle} />
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Remove master account?</h2>
              <button className={styles.closeBtn} onClick={closeModal}>
                <X size={18} />
              </button>
            </div>
            <hr className={styles.headerDivider} />

            <div className={`${styles.modalBody} ${mStyles.removeBody}`}>
              <p className={mStyles.removeDesc}>
                Removing this master account might affect connected accounts.
              </p>
              <p className={mStyles.removeSub}>This cannot be undone.</p>
              {removeError && (
                <p className={mStyles.fieldError} style={{ marginTop: 8 }}>{removeError}</p>
              )}
            </div>

            <div
              className={`${styles.modalFooter} ${mStyles.removeFooter} ${mStyles.insetFooter}`}
            >
              <button
                className={mStyles.cancelBtn}
                onClick={() => setModal("edit")}
                disabled={removeLoading}
              >
                Cancel
              </button>
              <button className={mStyles.removeConfirmBtn} onClick={handleRemove} disabled={removeLoading}>
                {removeLoading ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TradeCopierMaster;
